import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography,
  Box,
  FormGroup,
  FormControlLabel,
  Switch,
  Collapse,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';

import IconButton from 'components/IconButton';

const useStyles = makeStyles((theme) => ({
  categorySection: {
    marginBottom: theme.spacing(1),
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  requiredBadge: {
    display: 'inline-block',
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.secondary,
    padding: '2px 8px',
    borderRadius: 4,
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  button: {
    whiteSpace: 'nowrap',
    '&:focus-visible': {
      outline: '2px solid black',
      outlineOffset: 2,
    },
  },
}));

const ModalCategorySection = ({
  disabled,
  required,
  label,
  description,
  checked,
  onChange,
}) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);

  const categoryId = `${label.toLowerCase().replace(' ', '-')}-category`;

  const categoryDescription = `${
    expanded ? 'Hide' : 'Show'
  } description for ${label}`;

  const switchAriaLabel = required
    ? `${label}, required, always enabled`
    : `${label}, ${checked ? 'enabled' : 'disabled'}`;

  const badgeId = `${categoryId}-required-badge`;

  function toggleSection() {
    setExpanded((prev) => !prev);
  }

  return (
    <Box className={classes.categorySection}>
      <Box className={classes.categoryHeader}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={checked}
                onChange={(e) => onChange?.(e.target.checked)}
                disabled={disabled}
                inputProps={{
                  'aria-label': switchAriaLabel,
                  'aria-describedby': required ? badgeId : undefined,
                }}
              />
            }
            label={
              <span>
                {label}{' '}
                {required && (
                  <span className={classes.requiredBadge} id={badgeId}>
                    Required
                  </span>
                )}
              </span>
            }
          />
        </FormGroup>
        {description && (
          <IconButton
            size="small"
            onClick={toggleSection}
            aria-expanded={expanded}
            aria-controls={categoryId}
            aria-label={categoryDescription}
            className={classes.button}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}
      </Box>
      {description && (
        <Collapse in={expanded} id={categoryId}>
          <Typography variant="body2" color="textSecondary">
            {description}
          </Typography>
        </Collapse>
      )}
    </Box>
  );
};

ModalCategorySection.propTypes = {
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  label: PropTypes.string,
  description: PropTypes.string,
  checked: PropTypes.bool,
  onChange: PropTypes.func,
};

ModalCategorySection.defaultProps = {
  disabled: false,
  required: false,
  label: '',
  description: '',
  checked: true,
  onChange: () => {},
};

export default ModalCategorySection;
